import type {
  ConstraintDragState,
  OriginalDiagramAsset,
  DiagramPoint,
  DiagramSemanticProfile,
  DiagramSpec,
  DiagramTrustState,
  DiagramType,
} from '../types/diagramSpec';

export const DIAGRAM_POINT_INITIAL_PARAMETER = 0.3;

export const originalDiagramFixture: Readonly<OriginalDiagramAsset> = Object.freeze({
  assetRef: 'fixture://diagram-originals/quadratic-v0.1',
  fixtureId: 'diagram-quadratic-original-v0.1',
  immutable: true,
  contentDescription: '受控原题图 fixture：二次函数与点 P 的原始拍照证据。',
});

export const diagramTypeLabels: Record<DiagramType, string> = {
  function: '二次函数',
  conic: '标准椭圆',
  triangle: '三角形动点',
  circle: '圆与辅助线',
};

export const diagramTrustStateCopy: Record<DiagramTrustState, { title: string; detail: string }> = {
  original: { title: '核对原题图', detail: '原图是事实依据，可缩放查看。' },
  redrawn: { title: '清晰重绘', detail: 'AI 重绘仅供理解参考。' },
  needs_confirmation: { title: '先确认图形关系', detail: '确认前不能进入交互讲解。' },
  unavailable: { title: '暂时无法可靠重绘', detail: '保留原图，不猜测关系或补造图形。' },
  generated_confirmation: { title: '确认生成的示意图', detail: '示意图不代表边长、角度或比例。' },
};

const createCircleTangentSpec = (): DiagramSpec => ({
  version: '1.0',
  status: 'confirmed',
  coordinateSystem: {
    type: 'geometry',
    viewport: { xMin: -5, xMax: 5, yMin: -2, yMax: 8 },
  },
  entities: [
    { id: 'main-circle', type: 'circle', label: '圆' },
    { id: 'diameter', type: 'segment', label: '直径' },
    { id: 'extended-line', type: 'line', label: '延长线' },
    { id: 'tangent-1', type: 'line', label: '切线 1' },
    { id: 'tangent-2', type: 'line', label: '切线 2' },
  ],
  constraints: [],
  interactions: [],
  confidence: { overall: 'confirmed' },
  views: { original: true, redrawn: true },
  summary: '圆、直径、直径延长线与两条切线关系已按选中题目保留。',
});

export const createDiagramSpecForContext = (
  diagramType: DiagramType,
  semanticProfile: DiagramSemanticProfile = 'default'
): DiagramSpec => {
  if (
    diagramType === 'circle' &&
    semanticProfile === 'circle_diameter_extended_tangents'
  ) {
    return createCircleTangentSpec();
  }

  return {
    version: '1.0',
    status: 'confirmed',
    coordinateSystem: {
      type: diagramType === 'function' || diagramType === 'conic' ? 'cartesian' : 'geometry',
      viewport: { xMin: -5, xMax: 5, yMin: -2, yMax: 8 },
    },
    entities: [
      { id: 'main-shape', type: diagramType === 'function' ? 'function_curve' : diagramType === 'conic' ? 'conic' : diagramType === 'circle' ? 'circle' : 'segment' },
      { id: 'point-p', type: 'point', label: 'P' },
      { id: 'auxiliary', type: 'auxiliary_line' },
    ],
    constraints: [
      { id: 'point-p-constraint', type: diagramType === 'triangle' ? 'point_on_segment' : 'point_on_curve', entityIds: ['point-p', 'main-shape'], status: 'confirmed' },
    ],
    interactions: [
      { entityId: 'point-p', draggable: diagramType === 'function' || diagramType === 'triangle', constraintRef: 'point-p-constraint', resetParameter: DIAGRAM_POINT_INITIAL_PARAMETER },
    ],
    confidence: { overall: 'confirmed' },
    views: { original: true, redrawn: true },
    summary:
      diagramType === 'function'
        ? '二次函数，点 P 沿曲线移动。'
        : diagramType === 'triangle'
          ? '点 P 在线段 BC 上移动，辅助线 AD 随动。'
          : diagramType === 'conic'
            ? '标准椭圆，焦点与长轴关系已确认。'
            : '圆心、半径、弦、中点与辅助线关系已确认。',
  };
};

export const createDiagramSpecMock = (diagramType: DiagramType): DiagramSpec =>
  createDiagramSpecForContext(diagramType);

export const resolveDiagramSpecContext = (
  spec: DiagramSpec
): { diagramType: DiagramType; semanticProfile: DiagramSemanticProfile } | null => {
  const entityIds = new Set(spec.entities.map((entity) => entity.id));
  const entityTypes = new Set(spec.entities.map((entity) => entity.type));
  if (
    entityIds.has('main-circle') &&
    entityIds.has('diameter') &&
    entityIds.has('extended-line') &&
    entityIds.has('tangent-1') &&
    entityIds.has('tangent-2')
  ) {
    return {
      diagramType: 'circle',
      semanticProfile: 'circle_diameter_extended_tangents',
    };
  }
  if (entityTypes.has('circle')) {
    return { diagramType: 'circle', semanticProfile: 'default' };
  }
  if (entityTypes.has('conic')) {
    return { diagramType: 'conic', semanticProfile: 'default' };
  }
  if (entityTypes.has('function_curve')) {
    return { diagramType: 'function', semanticProfile: 'default' };
  }
  if (entityTypes.has('segment')) {
    return { diagramType: 'triangle', semanticProfile: 'default' };
  }
  return null;
};

export const projectPointToConstraint = (parameter: number): {
  parameter: number;
  point: DiagramPoint;
  dragState: ConstraintDragState;
} => {
  const clampedParameter = Math.max(0, Math.min(1, parameter));
  const x = 48 + clampedParameter * 244;
  const normalizedX = (clampedParameter - 0.5) * 2;
  const y = 184 - (1 - normalizedX * normalizedX) * 122;

  return {
    parameter: clampedParameter,
    point: { x, y },
    dragState: clampedParameter === parameter ? 'dragging' : 'boundary',
  };
};

export const resolveConstraintDragRelease = (startParameter: number, deltaX: number, dragDistance = 244): {
  parameter: number;
  dragState: 'boundary' | 'idle';
} => {
  const result = projectPointToConstraint(startParameter + deltaX / dragDistance);

  return {
    parameter: result.parameter,
    dragState: result.dragState === 'boundary' ? 'boundary' : 'idle',
  };
};

export const projectTrianglePoint = (parameter: number): DiagramPoint => {
  const clampedParameter = Math.max(0, Math.min(1, parameter));
  return { x: 78 + clampedParameter * 184, y: 196 };
};

export const resetDiagramPoint = (): { parameter: number; dragState: ConstraintDragState } => ({
  parameter: DIAGRAM_POINT_INITIAL_PARAMETER,
  dragState: 'reset',
});

export const invalid_constraint = 'invalid_constraint';
